<template>
  <div class="absence-page cabinet-page" :class="{ 'is-loading': loading }">
    <cabinet-page-header :eyebrow="group === 'vacation' ? $t('leave.timeAway') : $t('leave.healthFamily')" :title="pageTitle" :subtitle="pageSubtitle" />
    <app-page-loader v-if="loading" />
    <cabinet-state v-else-if="loadError" type="error" :title="$t('leave.unavailable')" :message="$t('leave.unavailableHelp')" @retry="loadPage" />
    <template v-else>
      <section class="absence-types">
        <button v-for="type in availableTypes" :key="type.code" type="button" class="absence-type-card" :class="{ active: selectedType && selectedType.code === type.code }" @click="selectType(type)">
          <span>{{ typeIcon(type.code) }}</span><strong>{{ $choice('leaveTypes', type) }}</strong><small v-if="type.rules || type.description">{{ type.rules || type.description }}</small>
        </button>
      </section>
      <div v-if="isUnpaid" class="absence-page__notice">{{ $t('leave.unpaidNotice') }}</div>
      <div v-if="isMaternity" class="absence-page__notice">
        {{ $t('leave.maternityNotice') }}
        <strong>{{ $t('leave.childBonusNotice') }}</strong>
      </div>
      <div v-if="isPaternity" class="absence-page__notice">
        {{ $t('leave.paternityNotice') }}
        <strong>{{ $t('leave.childBonusNotice') }}</strong>
      </div>
      <app-button v-if="availableTypes.length" class="btn--stretch absence-page__action" theme="request" :disabled="!selectedType" @click.native="openForm()">{{ actionLabel }}</app-button>
      <cabinet-state v-if="!availableTypes.length" type="empty" :title="$t('leave.noTypes')" :message="$t('leave.noTypesHelp')" />
      <section class="absence-history">
        <h2>{{ $t('leave.history', { type: group === 'vacation' ? $t('leave.leaveHistory') : $t('leave.medicalHistory') }) }}</h2>
        <cabinet-state v-if="!filteredRequests.length" type="empty" :title="$t('leave.noRequests')" :message="$t('common.requestsAppear')" />
        <article v-for="request in filteredRequests" v-else :key="request.id" class="absence-card">
          <div><div><cabinet-status :status="request.status" /><h3>{{ $choice('leaveTypes', request.type) }}</h3></div><strong>{{ request.days }} {{ request.days === 1 ? $t('common.day') : $t('common.daysPlural') }}</strong></div>
          <dl><dt>{{ $t('common.status') }}</dt><dd>{{ $choice('statuses', request.status) }}</dd><dt>{{ $t('common.dateFrom') }}</dt><dd>{{ formatDate(request.from_date) }}</dd><dt>{{ $t('common.dateTo') }}</dt><dd>{{ formatDate(request.to_date) }}</dd><dt>{{ $t('common.days') }}</dt><dd>{{ request.days }}</dd><template v-if="request.reason"><dt>{{ $t('common.comment') }}</dt><dd>{{ request.reason }}</dd></template><template v-if="request.decision_reason"><dt>{{ $t('common.decision') }}</dt><dd>{{ request.decision_reason }}</dd></template></dl>
          <div v-if="request.child_bonus" class="absence-card__bonus">{{ $t('leave.childBonus', { amount: formatAmount(request.child_bonus.amount), status: $choice('statuses', request.child_bonus.status) }) }}</div>
          <a v-if="request.proof" :href="request.proof.url" target="_blank" rel="noopener">{{ $t('leave.viewDocument') }}</a>
          <button v-if="canManageAttachment(request)" class="absence-card__attachment" type="button" @click="openAttachment(request)">{{ request.proof ? $t('leave.replaceDocument') : $t('leave.uploadDocument') }}</button>
          <button v-if="canCancel(request)" class="absence-card__cancel" type="button" :disabled="cancelling === request.id" @click="cancelRequest(request)">{{ cancelling === request.id ? $t('leave.cancelling') : $t('common.cancelRequest') }}</button>
        </article>
      </section>
    </template>
    <modal
      :name="modalName"
      :height="'auto'"
      :scrollable="true"
      :adaptive="true"
      :clickToClose="false"
      class="modal-not-overflow"
      :shiftY="0"
      transition="pop-out"
      @closed="onFormClosed"
    >
      <div class="popup">
        <button class="popup__close" type="button" :aria-label="$t('common.close')" @click="closeForm"></button>
        <div class="popup__title">{{ formTitle }}</div>
        <div class="popup__body">
          <form class="absence-form" @submit.prevent="submit">
            <app-form-group v-if="isParental" :label="isMaternity ? $t('leave.expectedBirth') : $t('leave.newbornBirthDate')" required>
              <date-picker
                v-model="form.parental_date"
                format="DD MMM YYYY"
                value-type="YYYY-MM-DD"
                :placeholder="isMaternity ? $t('leave.selectExpectedBirth') : $t('leave.selectBirth')"
                :lang="datePickerLanguage"
                title-format="DD MMM YYYY"
                class="datepicker"
                :class="{ 'datepicker--error': attempted && !validParentalDate }"
                :disabled-date="disableParentalDate"
                popup-class="datepicker__popup"
                @change="onParentalDateChanged"
              />
              <template #error>
                <div v-if="attempted && !form.parental_date">{{ $t('common.required') }}</div>
                <div v-else-if="attempted && !validParentalDate">{{ $t('leave.birthFuture') }}</div>
              </template>
            </app-form-group>
            <div v-if="isPaternity && unbornChild" class="absence-form__notice">{{ $t('leave.childNotBorn') }}</div>
            <app-form-group v-if="isSick" :label="$t('leave.fromDate')" required>
              <div class="absence-form__start-switch" role="radiogroup" :aria-label="$t('leave.sickStartLabel')">
                <button
                  type="button"
                  role="radio"
                  :aria-checked="form.from_date === today"
                  :class="{ active: form.from_date === today }"
                  @click="setSickStart(0)"
                >
                  {{ $t('leave.fromToday') }}
                </button>
                <button
                  type="button"
                  role="radio"
                  :aria-checked="form.from_date === yesterday"
                  :class="{ active: form.from_date === yesterday }"
                  @click="setSickStart(-1)"
                >
                  {{ $t('leave.fromYesterday') }}
                </button>
              </div>
            </app-form-group>
            <app-form-group v-else :label="isMaternity ? $t('leave.maternityFrom') : $t('leave.fromDate')" required>
              <date-picker
                v-model="form.from_date"
                format="DD MMM YYYY"
                value-type="YYYY-MM-DD"
                :placeholder="$t('leave.selectStart')"
                :lang="datePickerLanguage"
                title-format="DD MMM YYYY"
                class="datepicker"
                :class="{ 'datepicker--error': attempted && !form.from_date }"
                popup-class="datepicker__popup"
                :disabled-date="disableFromDate"
                @change="onFromDateChanged"
              />
              <template #error><div v-if="attempted && !form.from_date">{{ $t('common.required') }}</div></template>
            </app-form-group>
            <app-form-group :label="$t('leave.toDate')" required>
              <date-picker
                v-model="form.to_date"
                format="DD MMM YYYY"
                value-type="YYYY-MM-DD"
                :placeholder="$t('leave.selectEnd')"
                :lang="datePickerLanguage"
                title-format="DD MMM YYYY"
                class="datepicker"
                :class="{ 'datepicker--error': attempted && !validEndDate }"
                popup-class="datepicker__popup"
                :disabled="!form.from_date"
                :disabled-date="disableToDate"
              />
              <template #error>
                <div v-if="attempted && !form.to_date">{{ $t('common.required') }}</div>
                <div v-else-if="attempted && !validEndDate">{{ $t('leave.invalidPeriod') }}</div>
              </template>
            </app-form-group>
            <div v-if="form.from_date" class="absence-form__duration">
              {{ $t('leave.duration', { maximum: maximumDays, selected: selectedDays }) }}
            </div>
            <app-form-group v-if="requiresReason" :label="isParental ? $t('common.comment') : $t('common.reason')" required>
              <app-textarea v-model="form.reason" :placeholder="$t('leave.tellReason')" :error="attempted && !form.reason" />
              <template #error><div v-if="attempted && !form.reason">{{ $t('common.required') }}</div></template>
            </app-form-group>
            <app-form-group v-if="acceptsProofOnCreate" :label="attachmentLabel" :required="proofRequiredOnSubmit">
              <app-uploader v-model="form.proof" />
              <p class="absence-form__help">{{ proofOnCreateHelp }}</p>
              <template #error><div v-if="attempted && proofRequiredOnSubmit && !proofFileId">{{ $t('common.required') }}</div></template>
            </app-form-group>
            <app-button ref="submit" class="btn--stretch" theme="request" type="submit" :disabled="submitting">{{ $t('common.submitRequest') }}</app-button>
          </form>
        </div>
      </div>
    </modal>
    <modal name="leave-attachment" :height="'auto'" :adaptive="true" :clickToClose="false" class="modal-not-overflow" transition="pop-out">
      <div class="popup">
        <button class="popup__close" type="button" :aria-label="$t('common.close')" @click="closeAttachment"></button>
        <div class="popup__title">{{ attachmentRequest && attachmentRequest.proof ? $t('leave.replaceDocument') : $t('leave.uploadDocument') }}</div>
        <div class="popup__body">
          <p class="absence-form__help">{{ attachmentLaterHelp }}</p>
          <app-uploader v-model="attachmentFile" />
          <app-button v-if="attachmentFile && attachmentFile.id" ref="attachmentSubmit" class="btn--stretch absence-form__attachment-submit" :disabled="attachmentSaving" @click.native="saveAttachment">{{ $t('leave.saveAttachment') }}</app-button>
        </div>
      </div>
    </modal>
  </div>
</template>

<script>
import { cancelPersonalCabinetLeaveRequest, getPersonalCabinetLeaveRequests, getPersonalCabinetLeaveTypes, patchPersonalCabinetLeaveAttachment, postPersonalCabinetLeaveRequest } from '@/http'
import CabinetPageHeader from './CabinetPageHeader'
import CabinetState from './CabinetState'
import CabinetStatus from './CabinetStatus'
import { formatLocalizedDate, localeTag } from '@/i18n'

const SWAHILI_DATE_PICKER = {
  formatLocale: {
    months: ['Januari', 'Februari', 'Machi', 'Aprili', 'Mei', 'Juni', 'Julai', 'Agosti', 'Septemba', 'Oktoba', 'Novemba', 'Desemba'],
    monthsShort: ['Jan', 'Feb', 'Mac', 'Apr', 'Mei', 'Jun', 'Jul', 'Ago', 'Sep', 'Okt', 'Nov', 'Des'],
    weekdays: ['Jumapili', 'Jumatatu', 'Jumanne', 'Jumatano', 'Alhamisi', 'Ijumaa', 'Jumamosi'],
    weekdaysShort: ['Jpl', 'Jtt', 'Jnn', 'Jtn', 'Alh', 'Ijm', 'Jms'],
    weekdaysMin: ['Jp', 'Jt', 'Jn', 'Jt', 'Al', 'Ij', 'Jm'],
    firstDayOfWeek: 1,
  },
  yearFormat: 'YYYY',
  monthFormat: 'MMM',
  monthBeforeYear: true,
}

function parseDate(value) {
  return value ? new Date(`${value}T00:00:00`) : null
}

function dateOnly(value = new Date()) {
  const year = value.getFullYear()
  const month = String(value.getMonth() + 1).padStart(2, '0')
  const day = String(value.getDate()).padStart(2, '0')
  return `${year}-${month}-${day}`
}

function addDays(value, days) {
  const result = new Date(value)
  result.setDate(result.getDate() + days)
  return result
}

function addMonths(value, months) {
  const result = new Date(value)
  result.setMonth(result.getMonth() + months)
  return result
}

export default {
  name: 'PersonalCabinetAbsencePage',
  components: { CabinetPageHeader, CabinetState, CabinetStatus },
  props: { group: { type: String, required: true } },
  data() { return { loading: true, loadError: false, types: [], requests: [], selectedType: null, form: { parental_date: '', from_date: '', to_date: '', reason: '', proof: null }, attempted: false, submitting: false, cancelling: null, attachmentRequest: null, attachmentFile: null, attachmentSaving: false } },
  computed: {
    genderCode() {
      const gender = this.$store.state.personalCabinetData?.employee?.gender
      const code = typeof gender === 'string' ? gender : gender?.code
      return code ? String(code).toLowerCase() : null
    },
    availableTypes() {
      if (this.group !== 'medical') return this.types
      const allowedCodes = ['sick_leave']
      if (this.genderCode !== 'male') allowedCodes.push('maternity_leave')
      if (this.genderCode !== 'female') allowedCodes.push('paternity_leave')
      return this.types.filter(type => allowedCodes.includes(type.code))
    },
    isMaternity() { return this.selectedType?.code === 'maternity_leave' },
    isPaternity() { return this.selectedType?.code === 'paternity_leave' },
    isParental() { return this.isMaternity || this.isPaternity },
    isUnpaid() { return this.selectedType?.code === 'unpaid_leave' },
    isVacation() { return this.group === 'vacation' },
    isSick() { return this.selectedType?.code === 'sick_leave' },
    parentalMaximumDays() { return this.isMaternity ? 90 : 15 },
    maximumDays() { return this.isParental ? this.parentalMaximumDays : 28 },
    requiresReason() { return this.isSick || this.isPaternity },
    proofRequirement() { return this.selectedType?.proof_requirement || {} },
    proofRequiredOnSubmit() { return this.proofRequirement.required_on_submit === true || this.proofRequirement.code === 'required_on_submit' },
    acceptsProofOnCreate() { return this.proofRequiredOnSubmit || this.proofRequirement.accepted_on_create === true },
    proofFileId() { return this.form.proof?.id || null },
    attachmentLabel() { return this.isParental ? this.$t('leave.birthCertificate') : this.$t('common.supportingDocument') },
    proofOnCreateHelp() {
      if (this.proofRequiredOnSubmit) return this.$t('leave.documentRequiredOnSubmit')
      if (this.proofRequirement.code === 'required_later') return this.$t('leave.documentMayBeAddedLater')
      return this.$t('leave.documentOptional')
    },
    attachmentLaterHelp() {
      if (!this.attachmentRequest) return ''
      const requirement = this.requestProofRequirement(this.attachmentRequest)
      const documentRequired = requirement.required === true || ['required_later', 'required_on_submit'].includes(requirement.code)
      return this.$t(documentRequired ? 'leave.documentRequiredLater' : 'leave.documentOptionalLater')
    },
    selectedDays() {
      if (!this.form.from_date || !this.form.to_date) return 0
      const from = new Date(`${this.form.from_date}T00:00:00`)
      const to = new Date(`${this.form.to_date}T00:00:00`)
      return Math.max(Math.round((to - from) / 86400000) + 1, 0)
    },
    pageTitle() { return this.group === 'vacation' ? this.$t('leave.title') : this.$t('leave.medicalTitle') },
    pageSubtitle() { return this.group === 'vacation' ? this.$t('leave.vacationSubtitle') : this.$t('leave.medicalSubtitle') },
    actionLabel() {
      if (this.isMaternity) return this.$t('leave.requestMaternity')
      if (this.isPaternity) return this.$t('leave.requestPaternity')
      return this.group === 'vacation' ? this.$t('leave.requestLeave') : this.$t('leave.submitMedical')
    },
    formTitle() {
      if (this.selectedType) return this.$t('leave.newRequest', { type: this.$choice('leaveTypes', this.selectedType) })
      return this.group === 'vacation' ? this.$t('leave.newLeave') : this.$t('leave.newMedical')
    },
    modalName() { return `personal-cabinet-${this.group}-request` },
    filteredRequests() { return this.requests.filter(item => item.type && item.type.group === this.group) },
    validParentalDate() {
      if (!this.isParental) return true
      if (!this.form.parental_date) return false
      if (!this.isPaternity) return true
      const date = parseDate(this.form.parental_date)
      const today = parseDate(this.today)
      return date >= addMonths(today, -6) && date <= addMonths(today, 6)
    },
    unbornChild() { return this.isPaternity && this.form.parental_date > this.today },
    validFromDate() {
      if (!this.form.from_date) return false
      return !this.disableFromDate(parseDate(this.form.from_date))
    },
    validEndDate() { return this.form.to_date && this.validFromDate && this.form.to_date >= this.form.from_date && this.selectedDays <= this.maximumDays },
    formValid() { return this.selectedType && this.validParentalDate && this.validFromDate && this.validEndDate && (!this.requiresReason || this.form.reason.trim()) && (!this.proofRequiredOnSubmit || this.proofFileId) },
    today() { return dateOnly() },
    yesterday() { return dateOnly(addDays(parseDate(this.today), -1)) },
    datePickerLanguage() { return { en: 'en', sw: SWAHILI_DATE_PICKER }[this.$localeState.locale] },
  },
  created() { this.loadPage() },
  methods: {
    loadPage() {
      this.loading = true
      this.loadError = false
      return Promise.all([getPersonalCabinetLeaveTypes({ group: this.group }), getPersonalCabinetLeaveRequests()])
        .then(([types, requests]) => {
          this.types = Array.isArray(types.data) ? types.data : (types.data.results || [])
          this.requests = Array.isArray(requests.data) ? requests.data : (requests.data.results || [])
          this.applyQuerySelection()
        })
        .catch(() => { this.loadError = true })
        .finally(() => { this.loading = false })
    },
    applyQuerySelection() {
      const requested = this.$route.query.type
      let type = requested ? this.types.find(item => item.code === requested) : null
      if (requested === 'parental') type = this.availableTypes.find(item => ['maternity_leave', 'paternity_leave'].includes(item.code))
      if (!this.availableTypes.includes(type)) type = null
      if (!type) type = this.availableTypes[0]
      if (type) this.selectType(type)
    },
    selectType(type) { this.selectedType = type },
    openForm(type) {
      if (type) this.selectedType = type
      if (!this.selectedType) return
      this.form = { parental_date: '', from_date: '', to_date: '', reason: '', proof: null }
      if (this.isSick) this.setSickStart(0)
      this.attempted = false
      this.$nextTick(() => this.$modal.show(this.modalName))
    },
    closeForm() { this.$modal.hide(this.modalName) },
    onFormClosed() {
      this.attempted = false
    },
    typeIcon(code) { return code.includes('sick') ? '+' : code.includes('maternity') || code.includes('paternity') ? '♡' : code.includes('unpaid') ? '◒' : '☀' },
    formatDate(value) { return formatLocalizedDate(value, { day: '2-digit', month: 'short', year: 'numeric' }) },
    formatAmount(value) { return new Intl.NumberFormat(localeTag(), { maximumFractionDigits: 0 }).format(value || 0) },
    setSickStart(offset) {
      const fromDate = dateOnly(addDays(parseDate(this.today), offset))
      this.form.from_date = fromDate
      if (!this.form.to_date || this.form.to_date < fromDate || !this.validEndDate) this.form.to_date = fromDate
    },
    disableParentalDate(date) {
      if (!this.isPaternity) return false
      const today = parseDate(this.today)
      return date < addMonths(today, -6) || date > addMonths(today, 6)
    },
    disableFromDate(date) {
      const today = parseDate(this.today)
      if (this.isVacation) return date < today || date > addMonths(today, 12)
      if (this.isSick) return date < addDays(today, -1) || date > today
      if (this.isPaternity) {
        if (!this.form.parental_date) return true
        const birthDate = parseDate(this.form.parental_date)
        return date < addDays(birthDate, -3) || date > addMonths(birthDate, 6)
      }
      return false
    },
    disableToDate(date) {
      if (!this.form.from_date) return true
      const from = new Date(`${this.form.from_date}T00:00:00`)
      if (date < from) return true
      const maximum = new Date(from)
      maximum.setDate(maximum.getDate() + this.maximumDays - 1)
      return date > maximum
    },
    onFromDateChanged() {
      if (this.form.to_date && !this.validEndDate) this.form.to_date = ''
    },
    onParentalDateChanged() {
      if (this.form.from_date && !this.validFromDate) {
        this.form.from_date = ''
        this.form.to_date = ''
      }
    },
    submit() {
      this.attempted = true
      if (!this.formValid || this.submitting) return
      this.submitting = true
      this.$refs.submit.preload = true
      const payload = {
        type: this.selectedType.code,
        from_date: this.form.from_date,
        to_date: this.form.to_date,
        parental_date: this.isParental ? this.form.parental_date : null,
        reason: this.requiresReason ? this.form.reason.trim() : '',
        proof: this.acceptsProofOnCreate ? this.proofFileId : null,
      }
      return postPersonalCabinetLeaveRequest(payload)
        .then(({ data }) => {
          this.requests.unshift(data)
          this.selectedType = null
          this.closeForm()
          this.$root.$emit('personal-cabinet:requests-changed')
          this.$notify({ type: 'success', title: this.$t('leave.createdWithStatus', { status: this.$choice('statuses', data.status, this.$t('common.unknown')) }) })
        })
        .finally(() => {
          this.submitting = false
          if (this.$refs.submit) this.$refs.submit.preload = false
        })
    },
    canCancel(request) { return request.can_cancel === true || (request.can_cancel !== false && request.status && request.status.code === 'requested') },
    requestTypeDefinition(request) {
      const requestType = request.type || {}
      if (requestType.proof_requirement) return requestType
      const code = typeof requestType === 'string' ? requestType : requestType.code
      return this.types.find(type => type.code === code) || requestType
    },
    requestProofRequirement(request) { return this.requestTypeDefinition(request).proof_requirement || {} },
    statusCode(request) { return typeof request.status === 'string' ? request.status : request.status?.code },
    canManageAttachment(request) { return this.requestProofRequirement(request).can_upload_later === true && !['rejected', 'cancelled'].includes(this.statusCode(request)) },
    openAttachment(request) {
      if (!this.canManageAttachment(request)) return
      this.attachmentRequest = request
      this.attachmentFile = null
      this.$modal.show('leave-attachment')
    },
    closeAttachment() { if (this.attachmentSaving) return; this.$modal.hide('leave-attachment'); this.attachmentRequest = null; this.attachmentFile = null },
    saveAttachment() {
      if (!this.attachmentRequest || !this.canManageAttachment(this.attachmentRequest) || !this.attachmentFile?.id || this.attachmentSaving) return
      this.attachmentSaving = true
      this.$refs.attachmentSubmit.preload = true
      return patchPersonalCabinetLeaveAttachment(this.attachmentRequest.id, { proof: this.attachmentFile.id })
        .then(({ data }) => { const index = this.requests.findIndex(item => item.id === data.id); if (index !== -1) this.$set(this.requests, index, data); this.$modal.hide('leave-attachment'); this.attachmentRequest = null; this.attachmentFile = null; this.$notify({ type: 'success', title: this.$t('leave.attachmentSaved') }) })
        .finally(() => { this.attachmentSaving = false; if (this.$refs.attachmentSubmit) this.$refs.attachmentSubmit.preload = false })
    },
    cancelRequest(request) {
      this.cancelling = request.id
      return cancelPersonalCabinetLeaveRequest(request.id)
        .then(({ data }) => { const index = this.requests.findIndex(item => item.id === data.id); if (index !== -1) this.$set(this.requests, index, data); this.$root.$emit('personal-cabinet:requests-changed') })
        .finally(() => { this.cancelling = null })
    },
  },
}
</script>

<style lang="sass" scoped>
.absence-types
  display: grid
  grid-template-columns: repeat(2, minmax(0, 1fr))
  gap: 10px
  margin-bottom: 22px
  .absence-type-card
    display: flex
    flex-direction: column
    min-height: 128px
    padding: 14px
    border: 1px solid #e8e2d6
    border-radius: 18px
    background: #fffefa
    color: #37382f
    text-align: left
    &.active
      border-color: #a9963c
      box-shadow: 0 0 0 2px rgba(169, 150, 60, .12)
    > span
      display: grid
      place-items: center
      width: 38px
      height: 38px
      border-radius: 12px
      background: #efebdf
      color: #8e7d2d
      font-size: 20px
    strong
      margin-top: auto
      font-family: 'Mazzard Soft H', 'SFUIText', sans-serif
      font-size: 15px
      font-weight: 600
      line-height: 1.25
    small
      display: -webkit-box
      margin-top: 4px
      color: #96938b
      font-size: 9px
      line-height: 1.3
      -webkit-line-clamp: 2
      -webkit-box-orient: vertical
      overflow: hidden
.absence-page__action
  margin-bottom: 22px
.absence-page__notice
  margin: -10px 0 16px
  padding: 13px 15px
  border-radius: 12px
  background: #f4efe1
  color: #6f674f
  font-size: 12px
  line-height: 1.5
  strong
    display: block
    margin-top: 5px
.absence-form__duration
  margin: -7px 0 14px
  color: #827738
  font-size: 12px
.absence-form__start-switch
  display: grid
  grid-template-columns: repeat(2, minmax(0, 1fr))
  gap: 4px
  padding: 4px
  border: 1px solid #e4ded1
  border-radius: 14px
  background: #f1ede4
  button
    min-height: 44px
    padding: 8px 10px
    border-radius: 10px
    color: #7b7467
    font-family: 'Mazzard Soft H', 'SFUIText', sans-serif
    font-size: 13px
    font-weight: 600
    transition: background .16s ease, color .16s ease, box-shadow .16s ease
    &.active
      background: #77b017
      color: #fff
      box-shadow: 0 3px 9px rgba(119, 176, 23, .2)
.absence-form__notice,
.absence-form__help
  margin: 0 0 14px
  color: #7d7134
  font-size: 12px
  line-height: 1.45
.absence-form__notice
  padding: 11px 13px
  border-radius: 10px
  background: #fff4dc
.absence-form__attachment-submit
  margin-top: 16px
.absence-card__bonus
  margin-top: 10px
  padding: 9px 11px
  border-radius: 9px
  background: #f5edcf
  color: #786923
  font-size: 11px
  font-weight: 600
.absence-history > h2
  margin-bottom: 12px
  color: #6a6961
  font-size: 10px
  font-weight: 700
  letter-spacing: .12em
  text-transform: uppercase
.absence-card
  margin-top: 10px
  padding: 17px
  border: 1px solid #e8e2d6
  border-radius: 18px
  background: #fffefa
  > div
    display: flex
    justify-content: space-between
    gap: 12px
    h3
      margin-top: 7px
      font-family: 'Mazzard Soft H', 'SFUIText', sans-serif
      font-size: 18px
      font-weight: 600
      line-height: 1.25
    > strong
      color: #897b34
      font-size: 13px
  dl
    display: grid
    grid-template-columns: 30% 1fr
    margin: 13px 0 0
  dt, dd
    padding: 7px 0
    border-top: 1px solid #f0ece4
    font-size: 11px
    line-height: 1.4
  dt
    color: #99958d
  dd
    margin: 0
  a
    display: inline-block
    margin-top: 10px
    color: #87772d
    font-size: 11px
    font-weight: 600
.absence-card__cancel
  width: 100%
  margin-top: 14px
  padding: 10px
  border: 1px solid #e4b8af
  border-radius: 20px
  color: #b44a3b
  font-size: 11px
  font-weight: 700
.absence-card__attachment
  width: 100%
  margin-top: 12px
  padding: 10px
  border: 1px solid #a99a58
  border-radius: 20px
  color: #82742d
  font-size: 11px
  font-weight: 700
</style>
